import React, { useEffect, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { useAuth } from '../../../../firebase/AuthProvider';
import { CreateRefundAsync } from '../../../../services/PaymentServices/HelimCustomerService';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { useAbortManager } from '../../../../hooks/cancelletionManagerHook/useAbortManager';

interface RefundModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    chargeId: string;
    maxAmount: number;
}

const RefundModal: React.FC<RefundModalProps> = ({ isOpen, onClose, onSuccess, chargeId, maxAmount }) => {
    const { currentUser } = useAuth();
    const cancellationManager = useAbortManager();

    const [amount, setAmount] = useState<number>(0);
    const [reason, setReason] = useState<string>('');
    const [errors, setErrors] = useState<{ amount?: string; reason?: string }>({});
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        setAmount(previous => previous > maxAmount ? maxAmount : previous);
    }, [maxAmount]);

    const resetForm = () => {
        setAmount(0);
        setReason('');
        setErrors({});
        setLoading(false);
    };

    const validate = () => {
        const newErrors: { amount?: string; reason?: string } = {};
        if (amount <= 0) {
            newErrors.amount = 'Refund amount must be greater than 0.';
        } else if (amount > maxAmount) {
            newErrors.amount = `Refund amount cannot exceed ${maxAmount}.`;
        }
        if (!reason.trim()) {
            newErrors.reason = 'Reason is required.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        if (!currentUser) {
            toast.error('User not authenticated.');
            return;
        }

        const token = await currentUser.getIdToken();
        if (!token) {
            toast.error('Unable to get auth token.');
            return;
        }

        setLoading(true);
        const controller = cancellationManager.create(`refund-${chargeId}`);

        try {
            const payload = {
                chargeId,
                amount,
                reason
            };

            const res = await CreateRefundAsync(token, payload, controller.signal);
            if (res.success && res.data) {
                toast.success('Refund submitted successfully.');
                onSuccess();
                onClose();
                resetForm();
            } else {
                toast.error(res.message || 'Refund request failed.');
            }
        } catch (error: any) {
            if (error.name === 'AbortError') return;
            toast.error(error instanceof AxiosError ? error.response?.data?.message : error.message || 'Failed to process refund');
        } finally {
            setLoading(false);
            cancellationManager.remove(`refund-${chargeId}`);
        }
    };


    return (
        <Modal show={isOpen} onHide={onClose} centered className="custom_popup" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Refund Amount</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <label htmlFor="refundAmount" className="form-label">Refund Amount</label>
                        <input
                            id="refundAmount"
                            name="amount"
                            type="number"
                            min={0.01}
                            max={maxAmount}
                            step={0.01}
                            value={amount}
                            onChange={(e) => setAmount(Number(e.target.value))}
                            className="form-control"
                            disabled={loading}
                        />
                        <div className="form-text">Maximum refund amount: {maxAmount}</div>
                        {errors.amount && <div className="text-danger">{errors.amount}</div>}
                    </div>

                    <div className="mb-3">
                        <label htmlFor="refundReason" className="form-label">Reason</label>
                        <textarea
                            id="refundReason"
                            name="reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="form-control"
                            rows={3}
                            disabled={loading}
                        />
                        {errors.reason && <div className="text-danger">{errors.reason}</div>}
                    </div>

                    <div className="d-flex justify-content-end">
                        <Button variant="secondary" onClick={onClose} className="me-2" disabled={loading}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary">
                            {loading ? (
                                <>
                                    <Spinner size="sm" /> Processing...
                                </>
                            ) : (
                                'Submit Refund'
                            )}
                        </Button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default RefundModal;
